import {FC, ReactNode, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {GET, POST, PUT} from '../../../services/api'
import Swal from 'sweetalert2'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {CardInfoGuide, CardInfoContent} from '../../../components/CardInfoGuide/CardInfoGuide'
import usePluginsSpace from '../../../hooks/usePluginSpace'
import styles from './cashbackconfig.module.css'
import {
  accumulationRateInfo,
  accumulationGroupInfo,
  accumulationExpirationInfo,
  earningLimitsInfo,
  marginProtectionInfo,
  redemptionCapInfo,
  redemptionMinimumsInfo,
  redemptionBaseInfo,
  antifraudCooldownInfo,
  antifraudStackingInfo,
  antifraudAutoRefundInfo,
  walletCapInfo,
} from './components/cashbackCardInfoContent'

// -----------------------------------------------------------------------------
// Tipos
// -----------------------------------------------------------------------------

interface CashbackConfigData {
  id: number | null
  baseCashbackEnabled: boolean
  baseCashbackRate: number
  retailRate: number
  wholesaleRate: number
  creditExpirationDays: number
  earnOnShipping: boolean
  maxEarningPerOrder: number
  minOrderValueToEarn: number
  excludePromotionalItems: boolean
  onlySpecificRules: boolean
  maxRedemptionPercent: number
  minRedemptionValue: number
  minCartValueForUse: number
  includeShippingInBase: boolean
  cooldownDays: number
  allowStackingWithCoupons: boolean
  autoRefundEnabled: boolean
  maxWalletBalance: number
}

type CashbackConfigPayload = Omit<CashbackConfigData, 'id' | 'autoRefundEnabled'>

const DEFAULT_CONFIG: CashbackConfigData = {
  id: null,
  baseCashbackEnabled: true,
  baseCashbackRate: 0,
  retailRate: 0,
  wholesaleRate: 0,
  creditExpirationDays: 30,
  earnOnShipping: false,
  maxEarningPerOrder: 0,
  minOrderValueToEarn: 0,
  excludePromotionalItems: true,
  onlySpecificRules: false,
  maxRedemptionPercent: 30,
  minRedemptionValue: 5,
  minCartValueForUse: 0,
  includeShippingInBase: false,
  cooldownDays: 7,
  allowStackingWithCoupons: true,
  autoRefundEnabled: true,
  maxWalletBalance: 0,
}

const MAX_CASHBACK_MONEY_VALUE = 99999999.99

const formatCashbackMoneyLimit = () =>
  new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(
    MAX_CASHBACK_MONEY_VALUE
  )

const toNumber = (value: any, fallback: number) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const normalizeCashbackConfig = (payload: any): CashbackConfigData => ({
  id: payload?.id ?? null,
  baseCashbackEnabled:
    payload?.baseCashbackEnabled !== undefined
      ? !!payload.baseCashbackEnabled
      : DEFAULT_CONFIG.baseCashbackEnabled,
  baseCashbackRate: toNumber(payload?.baseCashbackRate, DEFAULT_CONFIG.baseCashbackRate),
  retailRate: toNumber(payload?.retailRate, DEFAULT_CONFIG.retailRate),
  wholesaleRate: toNumber(payload?.wholesaleRate, DEFAULT_CONFIG.wholesaleRate),
  creditExpirationDays: toNumber(
    payload?.creditExpirationDays,
    DEFAULT_CONFIG.creditExpirationDays
  ),
  earnOnShipping:
    payload?.earnOnShipping !== undefined
      ? !!payload.earnOnShipping
      : DEFAULT_CONFIG.earnOnShipping,
  maxEarningPerOrder: toNumber(payload?.maxEarningPerOrder, DEFAULT_CONFIG.maxEarningPerOrder),
  minOrderValueToEarn: toNumber(payload?.minOrderValueToEarn, DEFAULT_CONFIG.minOrderValueToEarn),
  excludePromotionalItems:
    payload?.excludePromotionalItems !== undefined
      ? !!payload.excludePromotionalItems
      : DEFAULT_CONFIG.excludePromotionalItems,
  onlySpecificRules:
    payload?.onlySpecificRules !== undefined
      ? !!payload.onlySpecificRules
      : DEFAULT_CONFIG.onlySpecificRules,
  maxRedemptionPercent: toNumber(
    payload?.maxRedemptionPercent,
    DEFAULT_CONFIG.maxRedemptionPercent
  ),
  minRedemptionValue: toNumber(payload?.minRedemptionValue, DEFAULT_CONFIG.minRedemptionValue),
  minCartValueForUse: toNumber(payload?.minCartValueForUse, DEFAULT_CONFIG.minCartValueForUse),
  includeShippingInBase:
    payload?.includeShippingInBase !== undefined
      ? !!payload.includeShippingInBase
      : DEFAULT_CONFIG.includeShippingInBase,
  cooldownDays: toNumber(payload?.cooldownDays, DEFAULT_CONFIG.cooldownDays),
  allowStackingWithCoupons:
    payload?.allowStackingWithCoupons !== undefined
      ? !!payload.allowStackingWithCoupons
      : DEFAULT_CONFIG.allowStackingWithCoupons,
  autoRefundEnabled:
    payload?.autoRefundEnabled !== undefined
      ? !!payload.autoRefundEnabled
      : DEFAULT_CONFIG.autoRefundEnabled,
  maxWalletBalance: toNumber(payload?.maxWalletBalance, DEFAULT_CONFIG.maxWalletBalance),
})

const buildCashbackConfigPayload = (config: CashbackConfigData): CashbackConfigPayload => ({
  baseCashbackEnabled: config.baseCashbackEnabled,
  baseCashbackRate: config.baseCashbackRate,
  retailRate: config.retailRate,
  wholesaleRate: config.wholesaleRate,
  creditExpirationDays: config.creditExpirationDays,
  earnOnShipping: config.earnOnShipping,
  maxEarningPerOrder: config.maxEarningPerOrder,
  minOrderValueToEarn: config.minOrderValueToEarn,
  excludePromotionalItems: config.excludePromotionalItems,
  onlySpecificRules: config.onlySpecificRules,
  maxRedemptionPercent: config.maxRedemptionPercent,
  minRedemptionValue: config.minRedemptionValue,
  minCartValueForUse: config.minCartValueForUse,
  includeShippingInBase: config.includeShippingInBase,
  cooldownDays: config.cooldownDays,
  allowStackingWithCoupons: config.allowStackingWithCoupons,
  maxWalletBalance: config.maxWalletBalance,
})

// -----------------------------------------------------------------------------
// CollapsibleCard
// -----------------------------------------------------------------------------

type CollapsibleCardProps = {
  title: string
  children: ReactNode
  defaultOpen?: boolean
  infoContent?: CardInfoContent
  infoInteraction?: 'click' | 'hover' | 'both'
}

const CollapsibleCard: FC<CollapsibleCardProps> = ({
  title,
  children,
  defaultOpen = true,
  infoContent,
  infoInteraction = 'click',
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className='card pb-5'>
      <div className='card-header py-5 d-flex justify-content-between align-items-center'>
        <h3 className='card-title align-items-start flex-column mb-0'>
          <span className='card-label fw-bold fs-4 mb-1'>{title}</span>
        </h3>
        <div className='d-flex align-items-center gap-2'>
          {infoContent && <CardInfoGuide content={infoContent} interaction={infoInteraction} />}
          <button
            type='button'
            className='btn btn-sm btn-icon cashback-card-toggle'
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Fechar card' : 'Abrir card'}
          >
            <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}></i>
          </button>
        </div>
      </div>
      <div
        style={{
          maxHeight: isOpen ? '2800px' : '0px',
          opacity: isOpen ? 1 : 0,
          overflow: isOpen ? 'visible' : 'hidden',
          transition: 'max-height 0.35s ease, opacity 0.25s ease',
        }}
      >
        <div className='px-10 py-5'>{children}</div>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Conteúdo principal
// -----------------------------------------------------------------------------

const Content: FC = () => {
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('#tab1')
  const {getPluginSpace} = usePluginsSpace()
  const [config, setConfig] = useState<CashbackConfigData>(DEFAULT_CONFIG)
  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [tabErrors, setTabErrors] = useState<{[key: string]: boolean}>({
    tab1: false,
    tab2: false,
    tab3: false,
  })

  const setField = <K extends keyof CashbackConfigData>(field: K, value: CashbackConfigData[K]) => {
    setConfig((prev) => ({...prev, [field]: value}))
    if (errors[field as string]) {
      const next = {...errors}
      delete next[field as string]
      setErrors(next)
    }
  }

  const setMoneyField = (
    field:
      | 'maxEarningPerOrder'
      | 'minOrderValueToEarn'
      | 'minRedemptionValue'
      | 'minCartValueForUse'
      | 'maxWalletBalance',
    value: number
  ) => {
    const safeValue = Math.min(Math.max(value ?? 0, 0), MAX_CASHBACK_MONEY_VALUE)
    setField(field, safeValue)
  }

  const getTabClass = (tabId: string, isActive: boolean) => {
    const baseClass = styles.tabItem
    if (tabErrors[tabId]) {
      return `${baseClass} ${styles.tabError} ${isActive ? 'active' : ''}`
    }
    return `${baseClass} ${isActive ? styles.tabActive : styles.tabInactive}`
  }

  // ---------------------------------------------------------------------
  // Carregamento
  // ---------------------------------------------------------------------

  const getConfig = async () => {
    try {
      const res = await GET('/cashback-config')
      const currentConfig = Array.isArray(res?.data)
        ? res.data[0]
        : Array.isArray(res?.data?.data)
        ? res.data.data[0]
        : res?.data?.data ?? res?.data

      if (currentConfig) {
        setConfig(normalizeCashbackConfig(currentConfig))
      } else {
        setConfig(DEFAULT_CONFIG)
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de cashback:', error)
    }
  }

  const init = async () => {
    try {
      setLoading(true)
      await getConfig()
    } catch (error) {
      console.error('Erro ao inicializar:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---------------------------------------------------------------------
  // Validação
  // ---------------------------------------------------------------------

  const validateForm = () => {
    const next: {[k: string]: string} = {}
    const nextTabErrors = {tab1: false, tab2: false, tab3: false}

    // Validações Tab 1
    if (config.baseCashbackEnabled) {
      if (config.baseCashbackRate === null || config.baseCashbackRate === undefined) {
        next['baseCashbackRate'] = 'Campo obrigatório'
        nextTabErrors.tab1 = true
      } else if (config.baseCashbackRate < 0 || config.baseCashbackRate > 100) {
        next['baseCashbackRate'] = 'O percentual deve estar entre 0 e 100'
        nextTabErrors.tab1 = true
      }
    }

    if (config.retailRate < 0 || config.retailRate > 100) {
      next['retailRate'] = 'O percentual deve estar entre 0 e 100'
      nextTabErrors.tab1 = true
    }

    if (config.wholesaleRate < 0 || config.wholesaleRate > 100) {
      next['wholesaleRate'] = 'O percentual deve estar entre 0 e 100'
      nextTabErrors.tab1 = true
    }

    if (!config.creditExpirationDays || config.creditExpirationDays <= 0) {
      next['creditExpirationDays'] = 'Informe um número de dias válido (maior que zero)'
      nextTabErrors.tab1 = true
    }

    if (config.maxEarningPerOrder === null || config.maxEarningPerOrder < 0) {
      next['maxEarningPerOrder'] = 'Informe um valor igual ou maior que zero'
      nextTabErrors.tab1 = true
    } else if (config.maxEarningPerOrder > MAX_CASHBACK_MONEY_VALUE) {
      next['maxEarningPerOrder'] = `O valor máximo permitido é ${formatCashbackMoneyLimit()}`
      nextTabErrors.tab1 = true
    }

    if (config.minOrderValueToEarn === null || config.minOrderValueToEarn < 0) {
      next['minOrderValueToEarn'] = 'Informe um valor igual ou maior que zero'
      nextTabErrors.tab1 = true
    } else if (config.minOrderValueToEarn > MAX_CASHBACK_MONEY_VALUE) {
      next['minOrderValueToEarn'] = `O valor máximo permitido é ${formatCashbackMoneyLimit()}`
      nextTabErrors.tab1 = true
    }

    // Validações Tab 2
    if (
      config.maxRedemptionPercent === null ||
      config.maxRedemptionPercent === undefined ||
      config.maxRedemptionPercent <= 0 ||
      config.maxRedemptionPercent > 100
    ) {
      next['maxRedemptionPercent'] = 'O percentual deve estar entre 1 e 100'
      nextTabErrors.tab2 = true
    }

    if (config.minRedemptionValue === null || config.minRedemptionValue < 0) {
      next['minRedemptionValue'] = 'Informe um valor igual ou maior que zero'
      nextTabErrors.tab2 = true
    } else if (config.minRedemptionValue > MAX_CASHBACK_MONEY_VALUE) {
      next['minRedemptionValue'] = `O valor máximo permitido é ${formatCashbackMoneyLimit()}`
      nextTabErrors.tab2 = true
    }

    if (config.minCartValueForUse === null || config.minCartValueForUse < 0) {
      next['minCartValueForUse'] = 'Informe um valor igual ou maior que zero'
      nextTabErrors.tab2 = true
    } else if (config.minCartValueForUse > MAX_CASHBACK_MONEY_VALUE) {
      next['minCartValueForUse'] = `O valor máximo permitido é ${formatCashbackMoneyLimit()}`
      nextTabErrors.tab2 = true
    }

    // Validações Tab 3
    if (config.cooldownDays === null || config.cooldownDays < 0) {
      next['cooldownDays'] = 'Informe um número de dias igual ou maior que zero'
      nextTabErrors.tab3 = true
    }

    if (config.maxWalletBalance === null || config.maxWalletBalance < 0) {
      next['maxWalletBalance'] = 'Informe um valor igual ou maior que zero'
      nextTabErrors.tab3 = true
    } else if (config.maxWalletBalance > MAX_CASHBACK_MONEY_VALUE) {
      next['maxWalletBalance'] = `O valor máximo permitido é ${formatCashbackMoneyLimit()}`
      nextTabErrors.tab3 = true
    }

    setErrors(next)
    setTabErrors(nextTabErrors)
    return Object.keys(next).length === 0
  }

  // ---------------------------------------------------------------------
  // Salvar
  // ---------------------------------------------------------------------

  const handleSave = async () => {
    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Campos obrigatórios',
        text: 'Verifique os campos destacados antes de salvar.',
      })
      return
    }

    try {
      setLoading(true)
      const payload = buildCashbackConfigPayload(config)
      if (config.id) {
        const res = await PUT(`/cashback-config/${config.id}`, payload)
        if (res?.success === false) {
          throw new Error(res?.message || 'Erro ao atualizar configurações de cashback')
        }
      } else {
        const res = await POST('/cashback-config', payload)
        if (res?.success === false) {
          throw new Error(res?.message || 'Erro ao criar configurações de cashback')
        }

        const createdConfig = res?.data ?? res?.dados ?? null
        if (createdConfig?.id) setConfig((prev) => ({...prev, id: createdConfig.id}))
      }

      Swal.fire({
        icon: 'success',
        title: 'Configurações salvas',
        text: 'As configurações foram salvas com sucesso!',
      })
    } catch (error) {
      console.error('Erro ao salvar configurações de cashback:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: String(error) || 'Erro ao salvar as configurações. Tente novamente.',
      })
    } finally {
      setLoading(false)
    }
  }

  const yesNoOptions = [
    {select: 'Sim', value: true},
    {select: 'Não', value: false},
  ]

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Configurações</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Defina as regras de acúmulo, resgate e proteção antifraude do seu programa de
                  cashback
                </span>
              </h3>
            </div>

            <div className='card-body'>
              {/* Abas */}
              <div className='d-flex justify-content-between'>
                <div className={`d-flex ${styles.tabMenu}`}>
                  <div
                    className={getTabClass('tab1', activeTab === '#tab1')}
                    onClick={() => setActiveTab('#tab1')}
                  >
                    <i className='fas fa-coins me-2'></i>
                    Regras de Acúmulo
                  </div>
                  <div
                    className={getTabClass('tab2', activeTab === '#tab2')}
                    onClick={() => setActiveTab('#tab2')}
                  >
                    <i className='fas fa-hand-holding-usd me-2'></i>
                    Regras de Resgate
                  </div>
                  <div
                    className={getTabClass('tab3', activeTab === '#tab3')}
                    onClick={() => setActiveTab('#tab3')}
                  >
                    <i className='fas fa-shield-alt me-2'></i>
                    Blindagem e Antifraude
                  </div>
                </div>
              </div>

              {/* Conteúdo das Abas */}
              <div>
                {/* -------------------------------------------------------- */}
                {/* Aba A — Regras de Acúmulo                                 */}
                {/* -------------------------------------------------------- */}
                {activeTab === '#tab1' && (
                  <div>
                    <CollapsibleCard
                      title='Taxa Base de Cashback'
                      infoContent={accumulationRateInfo}
                    >
                      <div className='row g-4 align-items-end'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Taxa base ativa'
                            value={config.baseCashbackEnabled}
                            change={(value: string | boolean) => {
                              // Converte a string que vem do HTML Select para um boolean real
                              const isEnabled = value === 'true' || value === true

                              setField('baseCashbackEnabled', isEnabled)

                              // Zera o percentual automaticamente se a pessoa desativar a base
                              if (!isEnabled) {
                                setField('baseCashbackRate', 0)
                                setField('retailRate', 0)
                                setField('wholesaleRate', 0)
                              }
                            }}
                            options={yesNoOptions}
                            required
                          />
                        </div>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'number'}}
                            label='Percentual da taxa base (%)'
                            value={String(config.baseCashbackRate)}
                            change={(value: string) =>
                              setField('baseCashbackRate', parseFloat(value) || 0)
                            }
                            required={config.baseCashbackEnabled}
                            disabled={!config.baseCashbackEnabled}
                            minNumber={0}
                            maxNumber={99.99}
                            allowFloat={true}
                          />
                          {errors['baseCashbackRate'] && (
                            <div className='text-danger small mt-1'>
                              {errors['baseCashbackRate']}
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>

                    {/* Deve ser usado para atacado, moda, varejo, food.. */}
                    <CollapsibleCard
                      title='Taxa Base por Tipo de Cliente'
                      infoContent={accumulationGroupInfo}
                    >
                      <div className='row g-4'>
                        <div className='col-md-6'>
                          <Input
                            inputAttr={{type: 'number'}}
                            label='Taxa CPF (%)'
                            value={String(config.retailRate)}
                            change={(value: string) =>
                              setField('retailRate', parseFloat(value) || 0)
                            }
                            disabled={!config.baseCashbackEnabled}
                            required
                            minNumber={0}
                            maxNumber={99.99}
                            allowFloat={true}
                          />
                          {errors['retailRate'] && (
                            <div className='text-danger small mt-1'>{errors['retailRate']}</div>
                          )}
                        </div>
                        <div className='col-md-6'>
                          <Input
                            inputAttr={{type: 'number'}}
                            label='Taxa CNPJ (%)'
                            value={String(config.wholesaleRate)}
                            change={(value: string) =>
                              setField('wholesaleRate', parseFloat(value) || 0)
                            }
                            disabled={!config.baseCashbackEnabled}
                            required
                            minNumber={0}
                            maxNumber={99.99}
                            allowFloat={true}
                          />
                          {errors['wholesaleRate'] && (
                            <div className='text-danger small mt-1'>{errors['wholesaleRate']}</div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>

                    <CollapsibleCard
                      title='Base e Limites de Acúmulo'
                      infoContent={earningLimitsInfo}
                    >
                      <div className='row g-4'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'money'}}
                            label='Teto financeiro por pedido (R$)'
                            value={config.maxEarningPerOrder}
                            change={(value: number) => setMoneyField('maxEarningPerOrder', value)}
                            required
                            currency='BRL'
                            locale='pt-BR'
                            alarm={config.maxEarningPerOrder > MAX_CASHBACK_MONEY_VALUE}
                            alarmText={`O valor máximo permitido é ${formatCashbackMoneyLimit()}`}
                          />
                          {errors['maxEarningPerOrder'] && (
                            <div className='text-danger small mt-1'>
                              {errors['maxEarningPerOrder']}
                            </div>
                          )}
                        </div>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'money'}}
                            label='Valor mínimo do pedido para gerar (R$)'
                            value={config.minOrderValueToEarn}
                            change={(value: number) => setMoneyField('minOrderValueToEarn', value)}
                            required
                            currency='BRL'
                            locale='pt-BR'
                            alarm={config.minOrderValueToEarn > MAX_CASHBACK_MONEY_VALUE}
                            alarmText={`O valor máximo permitido é ${formatCashbackMoneyLimit()}`}
                          />
                          {errors['minOrderValueToEarn'] && (
                            <div className='text-danger small mt-1'>
                              {errors['minOrderValueToEarn']}
                            </div>
                          )}
                        </div>
                        {/* <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Gerar cashback sobre valor do frete?'
                            value={config.earnOnShipping}
                            change={(value: boolean) => setField('earnOnShipping', value)}
                            options={yesNoOptions}
                            required
                          />
                        </div> */}
                      </div>
                    </CollapsibleCard>

                    <CollapsibleCard title='Proteção' infoContent={marginProtectionInfo}>
                      <div className='row g-4'>
                        <div className='col-md-6'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Gerar cashback apenas para itens com regras cadastradas?'
                            value={config.onlySpecificRules}
                            change={(value: boolean) => setField('onlySpecificRules', value)}
                            options={[
                              {select: 'Sim', value: true},
                              {select: 'Não', value: false},
                            ]}
                            required
                            marginTop='0'
                          />
                        </div>
                      </div>
                    </CollapsibleCard>

                    <CollapsibleCard
                      title='Validade do Crédito'
                      infoContent={accumulationExpirationInfo}
                    >
                      <div className='row g-4'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'number'}}
                            label='Dias até o saldo expirar'
                            value={config.creditExpirationDays}
                            change={(value: number) => setField('creditExpirationDays', value || 0)}
                            required
                            minNumber={0}
                          />
                          {errors['creditExpirationDays'] && (
                            <div className='text-danger small mt-1'>
                              {errors['creditExpirationDays']}
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>
                  </div>
                )}

                {/* -------------------------------------------------------- */}
                {/* Aba B — Regras de Resgate                                 */}
                {/* -------------------------------------------------------- */}
                {activeTab === '#tab2' && (
                  <div>
                    <CollapsibleCard
                      title='Teto de Abatimento no Pedido'
                      infoContent={redemptionCapInfo}
                    >
                      <div className='row g-4'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'number'}}
                            label='Percentual máximo do pedido (%)'
                            value={config.maxRedemptionPercent}
                            change={(value: number) => setField('maxRedemptionPercent', value || 0)}
                            required
                            minNumber={0}
                            maxNumber={99}
                            allowFloat={true}
                          />
                          {errors['maxRedemptionPercent'] && (
                            <div className='text-danger small mt-1'>
                              {errors['maxRedemptionPercent']}
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>

                    <CollapsibleCard
                      title='Valores Mínimos de Resgate'
                      infoContent={redemptionMinimumsInfo}
                    >
                      <div className='row g-4'>
                        <div className='col-md-6'>
                          <Input
                            inputAttr={{type: 'money'}}
                            label='Saldo mínimo para uso (R$)'
                            value={config.minRedemptionValue}
                            change={(value: number) => setMoneyField('minRedemptionValue', value)}
                            required
                            currency='BRL'
                            locale='pt-BR'
                            alarm={config.minRedemptionValue > MAX_CASHBACK_MONEY_VALUE}
                            alarmText={`O valor máximo permitido é ${formatCashbackMoneyLimit()}`}
                          />
                          {errors['minRedemptionValue'] && (
                            <div className='text-danger small mt-1'>
                              {errors['minRedemptionValue']}
                            </div>
                          )}
                        </div>
                        <div className='col-md-6'>
                          <Input
                            inputAttr={{type: 'money'}}
                            label='Valor mínimo do carrinho (R$)'
                            value={config.minCartValueForUse}
                            change={(value: number) => setMoneyField('minCartValueForUse', value)}
                            currency='BRL'
                            locale='pt-BR'
                            required
                            alarm={config.minCartValueForUse > MAX_CASHBACK_MONEY_VALUE}
                            alarmText={`O valor máximo permitido é ${formatCashbackMoneyLimit()}`}
                          />
                          {errors['minCartValueForUse'] && (
                            <div className='text-danger small mt-1'>
                              {errors['minCartValueForUse']}
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>

                    {/* <CollapsibleCard title='Base de Cálculo' infoContent={redemptionBaseInfo}>
                      <div className='row g-4'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='O cashback incide sobre o frete?'
                            value={config.includeShippingInBase}
                            change={(value: boolean) => setField('includeShippingInBase', value)}
                            options={yesNoOptions}
                            required
                          />
                        </div>
                      </div>
                    </CollapsibleCard> */}
                  </div>
                )}

                {/* -------------------------------------------------------- */}
                {/* Aba C — Blindagem e Antifraude                            */}
                {/* -------------------------------------------------------- */}
                {activeTab === '#tab3' && (
                  <div>
                    <CollapsibleCard
                      title='Carência de Liberação'
                      infoContent={antifraudCooldownInfo}
                    >
                      <div className='row g-4'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'number'}}
                            label='Dias de carência até liberar o saldo'
                            value={config.cooldownDays}
                            change={(value: number) => setField('cooldownDays', value || 0)}
                            required
                            minNumber={0}
                          />
                          {errors['cooldownDays'] && (
                            <div className='text-danger small mt-1'>{errors['cooldownDays']}</div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>

                    <CollapsibleCard
                      title='Acúmulo de Benefícios'
                      infoContent={antifraudStackingInfo}
                    >
                      <div className='row g-4'>
                        {/* <div className='col-md-6'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Permitir uso de cashback junto com cupons?'
                            value={config.allowStackingWithCoupons}
                            change={(value: boolean) => setField('allowStackingWithCoupons', value)}
                            options={yesNoOptions}
                            required
                          />
                        </div> */}
                        <div className='col-md-6'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Gerar cashback em itens promocionais?'
                            value={config.excludePromotionalItems}
                            change={(value: boolean) => setField('excludePromotionalItems', value)}
                            options={[
                              {select: 'Sim', value: false},
                              {select: 'Não', value: true},
                            ]}
                            required
                          />
                        </div>
                      </div>
                    </CollapsibleCard>

                    {/* <CollapsibleCard
                      title='Estorno Automático'
                      infoContent={antifraudAutoRefundInfo}
                    >
                      <div className='row g-4'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Estornar ao cancelar pedido?'
                            value={config.autoRefundEnabled}
                            change={(value: boolean) => setField('autoRefundEnabled', value)}
                            options={yesNoOptions}
                            required
                          />
                        </div>
                      </div>
                    </CollapsibleCard> */}

                    <CollapsibleCard title='Limite da Carteira' infoContent={walletCapInfo}>
                      <div className='row g-4'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'money'}}
                            label='Saldo máximo na carteira (R$)'
                            value={config.maxWalletBalance}
                            change={(value: number) => setMoneyField('maxWalletBalance', value)}
                            required
                            currency='BRL'
                            locale='pt-BR'
                            alarm={config.maxWalletBalance > MAX_CASHBACK_MONEY_VALUE}
                            alarmText={`O valor máximo permitido é ${formatCashbackMoneyLimit()}`}
                          />
                          {errors['maxWalletBalance'] && (
                            <div className='text-danger small mt-1'>
                              {errors['maxWalletBalance']}
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>
                  </div>
                )}
              </div>
            </div>

            <div className='card-footer border-0 pt-0 d-flex justify-content-end py-6 px-9'>
              <Button
                text='Salvar'
                color='primary'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={handleSave}
                disabled={loading}
                btnClass='fw-bolder'
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const CashbackConfig: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Cashback', path: 'cashback', isActive: false},
          {title: 'Configurações', path: 'cashback-config', isActive: true},
        ]}
      >
        Configurações
      </PageTitle>
      <Content />
    </>
  )
}

export {CashbackConfig}
